import React, { useState, useEffect } from "react";
import { Label, Button, Card, Checkbox, TextInput } from "flowbite-react";
import { toast } from "react-hot-toast";
import { settingsApi } from "../../api/settings";
import { useAuth } from "../../context/AuthContext";
// Import logos if available
import GCashLogo from "../../assets/icons/popup/GCASH-Logo.svg?react"; // Replace with HitPay logo if you have it
import MayaLogo from "../../assets/icons/popup/MAYA-Logo.svg?react";
import QRPHLogo from "../../assets/icons/popup/QR_Ph_Logo.svg?react";
import QRPHLogoPlus from "../../assets/icons/popup/QR_Ph_Logo_Plus.svg?react";
import GCashPlusLogo from "../../assets/icons/popup/gcash-plus-large.svg?react";
import PayWalletLogo from "../../assets/icons/popup/paywallet-logo.svg?react";
import PayWalletXLogo from "../../assets/icons/popup/paywalletx-logo.svg?react";

const PAYMENT_GATEWAY_KEYS = {
  PHMPAY_QRPH: "phmpay_qrph",
  PHMPAY_GCASHPLUS: "phmpay_gcashplus",
};

const LEGACY_PHMPAY_KEYS = ["phmpay"];

const resolveMinAmount = (minAmounts, gateway) => {
  if (minAmounts?.[gateway.key]) {
    return minAmounts[gateway.key];
  }

  if (
    (gateway.key === PAYMENT_GATEWAY_KEYS.PHMPAY_QRPH ||
      gateway.key === PAYMENT_GATEWAY_KEYS.PHMPAY_GCASHPLUS) &&
    minAmounts?.phmpay
  ) {
    return minAmounts.phmpay;
  }

  if (gateway.key === "paywalletx" && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  if (gateway.key === "paywallet_gcash" && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  if (gateway.key === "paywallet_storeqr" && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  return gateway.defaultMinAmount;
};

const normalizeGatewayKeys = (gatewayKeys = []) => {
  const normalized = gatewayKeys.flatMap((gateway) => {
    if (gateway === "phmpay") {
      return [PAYMENT_GATEWAY_KEYS.PHMPAY_QRPH];
    }

    return gateway;
  });

  return [...new Set(normalized)];
};

const GATEWAYS = [
  {
    key: "gcash",
    label: "GCash",
    Logo: GCashLogo,
    defaultMinAmount: 50,
  },
  {
    key: "maya",
    label: "Maya",
    Logo: MayaLogo,
    defaultMinAmount: 50,
  },
  {
    key: "qrph",
    label: "QRPH",
    Logo: QRPHLogo,
    defaultMinAmount: 2000,
  },
  {
    key: PAYMENT_GATEWAY_KEYS.PHMPAY_QRPH,
    label: "QRPH (MPay)",
    Logo: QRPHLogo, // Add logo later if available
    defaultMinAmount: 50,
  },
  {
    key: PAYMENT_GATEWAY_KEYS.PHMPAY_GCASHPLUS,
    label: "GCash+",
    Logo: GCashPlusLogo,
    defaultMinAmount: 50,
  },
  {
    key: "paywallet",
    label: "Paywallet - MPay",
    Logo: PayWalletLogo,
    defaultMinAmount: 50,
  },
  {
    key: "paywalletx",
    label: "PaywalletX - Payloro",
    Logo: PayWalletXLogo,
    defaultMinAmount: 50,
  },
  {
    key: "paywallet_gcash",
    label: "GCASH-NOW - Paywallet",
    Logo: GCashPlusLogo,
    defaultMinAmount: 50,
  },
  {
    key: "paywallet_storeqr",
    label: "QRPH Store QR - Paywallet (TapIt)",
    Logo: PayWalletLogo,
    defaultMinAmount: 50,
  },
];

export default function PaymentGatewaySettings() {
  const { user, tokens } = useAuth();
  const [gateways, setGateways] = useState([]); // Array of selected gateways
  const [gatewayMinAmounts, setGatewayMinAmounts] = useState({}); // Object with gateway min amounts
  const [manualDepositEnabled, setManualDepositEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        // Fetch payment gateway settings
        const gatewayResponse = await settingsApi.getSettings(
          tokens.access.token,
          user.id,
          "activePaymentGateway"
        );
        
        if (gatewayResponse.status === 200 && gatewayResponse.data) {
          const otherSettings = gatewayResponse.data.other_settings || gatewayResponse.data.otherSettings;
          if (otherSettings && Array.isArray(otherSettings.gateways)) {
            const normalizedGateways = normalizeGatewayKeys(otherSettings.gateways);
            setGateways(normalizedGateways);
            
            // Set minimum amounts from settings or use defaults
            const minAmounts = {};
            GATEWAYS.forEach(gateway => {
              minAmounts[gateway.key] = resolveMinAmount(otherSettings.minAmounts, gateway);
            });
            setGatewayMinAmounts(minAmounts);
          } else if (gatewayResponse.data.value) {
            const normalizedGateways = normalizeGatewayKeys(
              Array.isArray(gatewayResponse.data.value)
                ? gatewayResponse.data.value
                : gatewayResponse.data.value.split(',').map(g => g.trim()).filter(Boolean)
            );
            setGateways(normalizedGateways);
            
            // Use default amounts if no settings found
            const minAmounts = {};
            GATEWAYS.forEach(gateway => {
              minAmounts[gateway.key] = gateway.defaultMinAmount;
            });
            setGatewayMinAmounts(minAmounts);
          } else {
            setGateways([]);
            // Use default amounts
            const minAmounts = {};
            GATEWAYS.forEach(gateway => {
              minAmounts[gateway.key] = gateway.defaultMinAmount;
            });
            setGatewayMinAmounts(minAmounts);
          }
        }

        // Fetch manual deposit setting
        try {
          const manualDepositResponse = await settingsApi.getSettings(
            tokens.access.token,
            user.id,
            "manualDepositEnabled"
          );
          
          if (manualDepositResponse.status === 200 && manualDepositResponse.data) {
            setManualDepositEnabled(manualDepositResponse.data.value === "true");
          }
        } catch (error) {
          console.log("Manual deposit setting not found, defaulting to false");
          setManualDepositEnabled(false);
        }
        
      } catch (error) {
        toast.error("Failed to load payment settings");
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [tokens.access.token, user.id]);

  const handleCheckboxChange = (gateway) => {
    setGateways((prev) =>
      prev.includes(gateway)
        ? prev.filter((g) => g !== gateway)
        : [...prev, gateway]
    );
  };

  const handleMinAmountChange = (gateway, amount) => {
    setGatewayMinAmounts((prev) => ({
      ...prev,
      [gateway]: parseInt(amount) || 0,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Save payment gateway settings
      const value = gateways.length > 0 ? "true" : "false";
      // Only allow valid gateway keys
      const validGatewayKeys = new Set([
        "gcash",
        "maya",
        "qrph",
        PAYMENT_GATEWAY_KEYS.PHMPAY_QRPH,
        PAYMENT_GATEWAY_KEYS.PHMPAY_GCASHPLUS,
        ...LEGACY_PHMPAY_KEYS,
        "paywallet",
        "paywalletx",
        "paywallet_gcash",
        "paywallet_storeqr",
      ]);
      const filteredGateways = gateways.filter((gateway) => validGatewayKeys.has(gateway));

      const filteredMinAmounts = Object.fromEntries(
        Object.entries(gatewayMinAmounts).filter(([gateway]) => validGatewayKeys.has(gateway))
      );
      const otherSettings = { 
        gateways: filteredGateways,
        minAmounts: filteredMinAmounts
      };
      
      const gatewayResponse = await settingsApi.settings(
        tokens.access.token,
        user.id,
        "activePaymentGateway",
        value,
        otherSettings
      );

      // Save manual deposit setting
      const manualDepositResponse = await settingsApi.settings(
        tokens.access.token,
        user.id,
        "manualDepositEnabled",
        manualDepositEnabled ? "true" : "false"
      );
      
      if (gatewayResponse.status === 201 && manualDepositResponse.status === 201) {
        toast.success("Payment settings updated successfully");
      } else {
        toast.error("Failed to update some payment settings");
      }
    } catch (error) {
      toast.error("Failed to update payment settings");
      console.error("Error saving settings:", error);
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-1">Payment Gateway Settings</h3>
          <p className="text-gray-500 text-sm">
            Configure payment gateways, minimum amounts, and deposit options.
          </p>
        </div>

        {/* Manual Deposit Toggle */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Manual Deposit</h4>
              <p className="text-sm text-gray-500">Allow users to request manual deposits</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={manualDepositEnabled}
                onChange={(e) => setManualDepositEnabled(e.target.checked)}
                disabled={loading}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
            </label>
          </div>
        </div>

        {/* Payment Gateways */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-4">Select Payment Gateways</h4>
          <div className="space-y-4">
            {GATEWAYS.map(({ key, label, Logo }) => {
              const selected = gateways.includes(key);
              return (
                <div
                  key={key}
                  className={`p-4 rounded-lg border transition-all duration-150 ${
                    selected ? "bg-blue-50 border-blue-500" : "bg-white border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Checkbox and Logo */}
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={key}
                        checked={selected}
                        onChange={() => handleCheckboxChange(key)}
                        disabled={loading}
                      />
                      {Logo ? (
                        <Logo className="w-8 h-8" />
                      ) : (
                        <span className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full text-sm font-bold text-gray-700">
                          {label[0]}
                        </span>
                      )}
                      <label
                        htmlFor={key}
                        className={`font-medium text-base cursor-pointer ${
                          selected ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {label}
                      </label>
                    </div>

                    {/* Minimum Amount Input */}
                    <div className="flex-1 max-w-xs">
                      <Label htmlFor={`${key}-amount`} className="text-sm text-gray-600">
                        Minimum Amount (₱)
                      </Label>
                      <TextInput
                        id={`${key}-amount`}
                        type="number"
                        value={gatewayMinAmounts[key] || ""}
                        onChange={(e) => handleMinAmountChange(key, e.target.value)}
                        disabled={loading || !selected}
                        placeholder="Enter minimum amount"
                        min="1"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Button
          className="w-full text-base font-semibold"
          onClick={handleSave}
          disabled={loading || saving}
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </Card>
    </div>
  );
} 